import React from 'react';
import moment from 'moment';
import { envelopesByDoc, splitCell, weekEnding } from './billingTicketDocs';
import { weekTracker } from './billingWeekTracker';
import { invoicedWeeks, summaryWeeksOf } from './billingInvoiceWorkbook';

// Labor & Equipment's week picker, in place of the date range (Seth 11/4:
// "make it like the invoice workbook tab where it shows week period and
// whether they have been sent or have weekly summaries signed" / "like what we
// do on daily field ticket page where we show weeks in a month and lets just
// show like the last two months … make the row height more condensed").
//
// Laid out as the Daily Field Tickets coverage grid is: one block per month,
// each day at its day-of-month column, and in each block a row for the weeks
// (click one to read it, shift-click to stretch the pick to it), the day and
// night field tickets by where their crew send stands, and the week summary
// over its days. Read off the same ticket documents and envelopes as Daily
// Field Tickets' week tracker and the Invoice Workbook's week list.

const iso = (m) => m.format('YYYY-MM-DD');
export const COL_W = 42;
const LABEL_W = 96;
const ROW_H = 22;

const TONES = {
    good: { bg: 'var(--success-tint)', fg: 'var(--success-text)' },
    out: { bg: 'var(--primary-tint)', fg: 'var(--primary)' },
    bad: { bg: 'var(--destructive-tint)', fg: 'var(--destructive-text)' },
    warn: { bg: 'var(--warning-tint)', fg: 'var(--warning-text)' },
    none: { bg: 'var(--muted)', fg: 'var(--text-secondary)' },
};
const SHEET = {
    Signed: { tone: 'good', word: 'Signed' },
    'Sent for Signature': { tone: 'out', word: 'Out' },
    Declined: { tone: 'bad', word: 'Declined' },
};
const sheetOf = (stage) => SHEET[stage] || { tone: 'warn', word: 'Not sent' };
const mdUtc = (x) => (x ? moment.utc(x).local().format('M/D') : '');
const md = (d) => moment(d, 'YYYY-MM-DD').format('M/D');

/**
 * The grid's model for the months from `from` (a 'YYYY-MM-DD' in the first
 * month shown) through the month of `today`:
 *   months     [{ key, name, days: [{ d, slot, future }], weeks: [{ we, a, b }] }]
 *              `a`/`b` the first and last day-of-month slot the week has in it
 *   sheets     { Day: { [d]: { tone, word, title } }, Night: { … } | null }
 *   summaries  [{ from, to, tone, label, title, key }]   one per summary envelope;
 *              a past week with dailies and no summary gets a "No summary" span
 *   weeks      { [we]: { title, invoiced } }
 */
export const reconWeekGrid = ({ docs = [], envelopes = [], from, today = iso(moment()) } = {}) => {
    const last = moment(weekEnding(today), 'YYYY-MM-DD').endOf('month');
    const first = moment(from || today, 'YYYY-MM-DD').startOf('month');
    const months = [];
    for (let m = first.clone(); m.isSameOrBefore(last, 'month'); m.add(1, 'month')) {
        const days = [];
        for (let d = m.clone().startOf('month'); d.isSameOrBefore(m.clone().endOf('month'), 'day'); d.add(1, 'day')) {
            days.push({ d: iso(d), slot: d.date() - 1, future: iso(d) > today });
        }
        const weeks = [];
        days.forEach((x) => {
            const we = weekEnding(x.d);
            const w = weeks[weeks.length - 1];
            if (w && w.we === we) w.b = x.slot;
            else weeks.push({ we, a: x.slot, b: x.slot });
        });
        months.push({ key: m.format('YYYY-MM'), name: m.format('MMMM YYYY'), days, weeks });
    }
    const allDays = months.flatMap((b) => b.days.map((x) => x.d));
    const startD = allDays[0];
    const endD = allDays[allDays.length - 1];
    const weekEnds = [...new Set(months.flatMap((b) => b.weeks.map((w) => w.we)))];

    const tracker = weekTracker({ docs, sigByDoc: envelopesByDoc(envelopes || []), envelopes: envelopes || [] });
    const sheets = { Day: {}, Night: {} };
    tracker.forEach((w) => (w.tickets.cells || []).forEach((c) => {
        const { d, sh } = splitCell(c.cell);
        if (d < startD || d > endD) return;
        const s = sheetOf(c.stage);
        (sheets[sh === 'Night' ? 'Night' : 'Day'])[d] = { ...s, title: `${sh === 'Night' ? 'Night' : 'Day'} ticket ${moment(d, 'YYYY-MM-DD').format('ddd M/D')}: ${s.word.toLowerCase()}` };
    }));
    const byWe = {};
    tracker.forEach((w) => { byWe[w.weekEnd] = w; });

    const sums = summaryWeeksOf(envelopes || [], docs || [], weekEnds);
    const seen = {};
    const summaries = [];
    weekEnds.forEach((we) => {
        const list = sums[we] || [];
        list.forEach((s) => {
            const key = String(s.env?.RowID || `${s.from}|${s.to}`);
            if (seen[key]) return;
            seen[key] = true;
            const tone = s.status === 'Signed' ? 'good' : s.status === 'Declined' ? 'bad' : 'out';
            const label = s.status === 'Signed' ? `Signed ${mdUtc(s.at)}` : s.status === 'Declined' ? 'Declined'
                : s.waitOn.length === 1 ? `Out · ${s.waitOn[0].split(' ')[0]}` : s.total ? `Out · ${s.signed} of ${s.total}` : `Out ${mdUtc(s.at)}`;
            const title = [
                `Week summary ${md(s.from)}–${md(s.to)}: ${s.status.toLowerCase()}${s.status === 'Signed' ? ` ${mdUtc(s.at)}` : ''}`,
                s.waitOn.length ? ` · waiting on ${s.waitOn.join(', ')}` : s.total && s.status !== 'Signed' ? ` · ${s.signed} of ${s.total} signed` : '',
                s.env?.Sent_At_Utc ? ` · sent ${mdUtc(s.env.Sent_At_Utc)}${s.env.Sent_By ? ` by ${s.env.Sent_By}` : ''}` : '',
            ].join('');
            summaries.push({ from: s.from, to: s.to, tone, label, title, key });
        });
        // A finished week whose dailies went out and has no summary yet.
        const t = byWe[we]?.tickets;
        if (!list.length && t && t.total && we < today) {
            summaries.push({ from: iso(moment(we, 'YYYY-MM-DD').subtract(6, 'days')), to: we, tone: 'warn', label: 'No summary', title: `No week summary has gone out for WE ${md(we)}`, key: `none${we}` });
        }
    });

    const inv = invoicedWeeks(envelopes || [], weekEnds);
    const weeks = {};
    weekEnds.forEach((we) => {
        const t = byWe[we]?.tickets;
        const daily = t && t.total
            ? `Daily tickets: ${[t.signed && `${t.signed} signed`, t.out && `${t.out} out`, t.draft && `${t.draft} not sent`, t.declined && `${t.declined} declined`].filter(Boolean).join(', ')}`
            : 'No daily tickets';
        const sumText = (sums[we] || []).map((s) => `Week summary: ${s.status.toLowerCase()}${s.status === 'Signed' ? ` ${mdUtc(s.at)}` : ''}`).join('\n') || 'No week summary';
        const invText = inv[we] ? `Invoiced: ${inv[we].map((x) => `${x.no} (${x.status === 'Out' ? 'out for approval' : x.status.toLowerCase()})`).join(', ')}` : '';
        weeks[we] = {
            invoiced: inv[we] || null,
            title: [`WE ${md(we)} (${md(iso(moment(we, 'YYYY-MM-DD').subtract(6, 'days')))}–${md(we)})`, daily, sumText, invText].filter(Boolean).join('\n'),
        };
    });
    return { months, sheets: { Day: sheets.Day, Night: Object.keys(sheets.Night).length ? sheets.Night : null }, summaries, weeks };
};

const CSS = `
.rw-wk { flex-shrink: 0; height: ${ROW_H - 2}px; margin: 0 1px; box-sizing: border-box; border-radius: 6px; border: 1px solid var(--border); background: var(--background); color: var(--foreground); font: inherit; font-size: 12px; font-weight: 500; line-height: ${ROW_H - 4}px; padding: 0 6px; display: inline-flex; align-items: center; justify-content: center; gap: 4px; white-space: nowrap; overflow: hidden; cursor: pointer; transition: background-color .15s, border-color .15s, box-shadow .15s; }
.rw-wk:hover { border-color: var(--border-strong); background: var(--muted); }
.rw-wk.on { background: var(--primary); border-color: var(--primary); color: var(--primary-foreground); }
.rw-wk:focus-visible { outline: none; border-color: var(--ring); box-shadow: var(--focus-ring); }
.rw-ghost { height: 28px; padding: 0 10px; border-radius: var(--radius); border: 1px solid transparent; background: transparent; font: inherit; font-size: 12px; font-weight: 500; color: var(--foreground); cursor: pointer; transition: background-color .15s; }
.rw-ghost:hover { background: var(--muted); }
.rw-ghost.on { background: var(--primary); color: var(--primary-foreground); }
.rw-ghost:focus-visible { outline: none; box-shadow: var(--focus-ring); }
.rw-cell { flex-shrink: 0; height: ${ROW_H - 2}px; margin: 0 1px; box-sizing: border-box; border-radius: 4px; border: 0; font: inherit; font-size: 11px; font-weight: 500; line-height: ${ROW_H - 2}px; padding: 0 4px; display: inline-flex; align-items: center; justify-content: center; gap: 4px; white-space: nowrap; overflow: hidden; cursor: pointer; transition: box-shadow .15s, filter .15s; }
.rw-cell:hover { filter: brightness(0.96); }
.rw-cell.on { box-shadow: inset 0 0 0 2px var(--primary); }
.rw-cell:focus-visible { outline: none; box-shadow: var(--focus-ring); }
.rw-day { width: ${COL_W}px; flex-shrink: 0; text-align: center; font: inherit; font-size: 11px; line-height: 16px; white-space: nowrap; border: 0; background: transparent; padding: 0; border-radius: 4px; }
button.rw-day { cursor: pointer; transition: background-color .15s, color .15s; }
button.rw-day:hover { background: var(--muted); }
button.rw-day.on { background: var(--primary); color: var(--primary-foreground) !important; }
button.rw-day:focus-visible { outline: none; box-shadow: var(--focus-ring); }
.rw-icon { position: relative; width: 32px; height: 32px; flex-shrink: 0; border-radius: var(--radius); border: 1px solid var(--border); background: var(--background); color: var(--foreground); box-shadow: var(--shadow-xs); display: inline-flex; align-items: center; justify-content: center; cursor: pointer; padding: 0; transition: background-color .15s, border-color .15s, box-shadow .15s; }
.rw-icon:hover { background: var(--muted); border-color: var(--border-strong); }
.rw-icon.open { background: var(--muted); }
.rw-icon:focus-visible { outline: none; border-color: var(--ring); box-shadow: var(--focus-ring); }
.rw-icon .rw-dot { position: absolute; top: 5px; right: 5px; width: 6px; height: 6px; border-radius: 9999px; background: var(--primary); }
.rw-menu { position: absolute; top: 36px; right: 0; z-index: 20; width: 312px; box-sizing: border-box; padding: 6px; background: var(--popover); border: 1px solid var(--border); border-radius: var(--radius-card); box-shadow: var(--shadow-md); }
.rw-menu-h { font-size: 12px; font-weight: 500; color: var(--muted-foreground); padding: 6px 6px 4px; }
.rw-menu-sep { height: 1px; background: var(--border); margin: 6px 0; }
.rw-menu-hint { font-size: 12px; color: var(--muted-foreground); padding: 2px 6px 4px; }
`;

const lbl = { fontSize: 12, fontWeight: 400, color: 'var(--text-secondary)', whiteSpace: 'nowrap' };
const sticky = { position: 'sticky', left: 0, zIndex: 1, background: 'var(--background)' };
const gap = (key, n) => <span key={key} style={{ width: COL_W * n, flexShrink: 0 }} />;

const Chip = ({ span = 1, tone, label, title }) => {
    const t = TONES[tone] || TONES.none;
    return (
        <span
            title={title}
            style={{
                flexShrink: 0, width: COL_W * span - 2, height: ROW_H - 2, margin: '0 1px', boxSizing: 'border-box', borderRadius: 4,
                background: t.bg, color: t.fg, fontSize: 11, fontWeight: 500, lineHeight: `${ROW_H - 2}px`, textAlign: 'center',
                whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', padding: '0 3px',
            }}
        >
            {label}
        </span>
    );
};

const Row = ({ label, title, children, top }) => (
    <div style={{ display: 'flex', alignItems: 'center', height: ROW_H, borderTop: top ? '1px solid var(--border)' : 'none' }}>
        <span title={title} style={{ ...lbl, ...sticky, width: LABEL_W, flexShrink: 0, lineHeight: `${ROW_H}px` }}>{label}</span>
        {children}
    </div>
);

/**
 * The picker. Click a week to take it in or out of the pick, shift-click to
 * take in every week back to the last one clicked; the grid below shows only
 * the picked weeks (Seth 11/4: "make the weeks selectable and then the rows
 * filtered based on weeks selected" / "or days if day field tickets we are
 * generating"). With `dayPick` (the Daily Field Tickets view) the days are
 * picked the same way, off the day row at the top of each month.
 *   picked   { unit: 'week' | 'day', keys } — the weeks or days shown lit
 * Each month: its days, the day and night tickets, the week summary and the
 * invoice, one cell a week (Seth 11/4: "make the lines … day ticket week
 * summary and then invoice and remove the week row").
 */
// This month only hides the months before this one, remembered on this
// browser (Seth 11/4: "can we condense this and maybe add a hide except for
// current month").
const ONLY_KEY = 'reconWeeks.thisMonthOnly';
const readOnly = () => { try { return window.localStorage.getItem(ONLY_KEY) === '1'; } catch (e) { return false; } };
const writeOnly = (on) => { try { window.localStorage.setItem(ONLY_KEY, on ? '1' : '0'); } catch (e) { /* storage blocked: it just isn't remembered */ } };

const TONE_RANK = { bad: 0, warn: 1, out: 2, none: 3, good: 4 };
const weekFrom = (we) => iso(moment(we, 'YYYY-MM-DD').subtract(6, 'days'));

// A funnel (Lucide "filter"), 16 px, in the text's own color.
const FilterGlyph = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
    </svg>
);

// The Weeks buttons (All weeks / This month / Earlier month) and whatever the
// page adds (`extra`, the Compare switch) sit in a menu behind a filter icon
// at the strip's right (Seth 11/4: "can we hide these in a filter menu that is
// an icon"). The icon carries a navy dot while a filter is set (`active`).
const FilterMenu = ({ hint, active, children }) => {
    const [open, setOpen] = React.useState(false);
    const box = React.useRef(null);
    React.useEffect(() => {
        if (!open) return undefined;
        const away = (e) => { if (box.current && !box.current.contains(e.target)) setOpen(false); };
        const esc = (e) => { if (e.key === 'Escape') setOpen(false); };
        document.addEventListener('mousedown', away);
        document.addEventListener('keydown', esc);
        return () => { document.removeEventListener('mousedown', away); document.removeEventListener('keydown', esc); };
    }, [open]);
    return (
        <div ref={box} style={{ position: 'relative', flexShrink: 0 }}>
            <button
                type="button"
                className={`rw-icon${open ? ' open' : ''}`}
                aria-label="Week filters"
                aria-haspopup="true"
                aria-expanded={open}
                title="Week filters"
                onClick={() => setOpen((v) => !v)}
            >
                <FilterGlyph />
                {active ? <span className="rw-dot" /> : null}
            </button>
            {open ? (
                <div className="rw-menu" role="group" aria-label="Week filters">
                    {children}
                    {hint ? <><div className="rw-menu-sep" /><div className="rw-menu-hint">{hint}</div></> : null}
                </div>
            ) : null}
        </div>
    );
};

const ReconWeekStrip = ({ grid, picked = { unit: 'week', keys: [] }, all, dayPick, onWeek, onDay, onAll, onMore, extra, extraActive }) => {
    const today = iso(moment());
    const [only, setOnly] = React.useState(readOnly);
    const setOnlyTo = (on) => { setOnly(on); writeOnly(on); };
    const thisMonth = today.slice(0, 7);
    const months = only ? grid.months.filter((b) => b.key >= thisMonth) : grid.months;
    const slots = months.reduce((n, b) => Math.max(n, b.days.length), 0);
    const lit = new Set(all ? [] : picked.keys);
    const weekOn = (we) => picked.unit === 'week' && lit.has(we);
    const dayOn = (d) => picked.unit === 'day' && lit.has(d);

    // A week's summaries (one week can hold two) and its invoices.
    const sumsOf = (we) => grid.summaries.filter((x) => x.from <= we && x.to >= weekFrom(we));
    const hasDailies = (we) => ['Day', 'Night'].some((sh) => grid.sheets[sh]
        && Object.keys(grid.sheets[sh]).some((d) => d >= weekFrom(we) && d <= we));
    const summaryCell = (we) => {
        const list = sumsOf(we);
        if (!list.length) return { tone: 'none', word: '', title: `No week summary for WE ${md(we)}` };
        const tone = list.map((x) => x.tone).sort((a, b) => TONE_RANK[a] - TONE_RANK[b])[0];
        return { tone, word: list.map((x) => x.label).join(' · '), title: list.map((x) => x.title).join('\n') };
    };
    const invoiceCell = (we) => {
        const inv = grid.weeks[we]?.invoiced;
        if (inv && inv.length) {
            const out = inv.some((x) => x.status === 'Out');
            return {
                tone: out ? 'out' : 'good',
                word: inv.map((x) => x.no).join(' · '),
                title: `Invoiced: ${inv.map((x) => `${x.no} (${x.status === 'Out' ? 'out for approval' : String(x.status || '').toLowerCase()})`).join(', ')}`,
            };
        }
        return we < today && hasDailies(we)
            ? { tone: 'warn', word: 'Not invoiced', title: `WE ${md(we)} is on no labor or equipment invoice yet` }
            : { tone: 'none', word: '', title: `WE ${md(we)}: not invoiced` };
    };

    const dayCells = (blk, render) => blk.days.map((x) => render(x));
    // One row of week cells; a click picks, shift-click takes in the run.
    const weekRow = (blk, cellOf, lead) => blk.weeks.map((w, i) => {
        const prev = blk.weeks[i - 1];
        const gapN = i === 0 ? w.a : w.a - prev.b - 1;
        const c = cellOf(w.we);
        const t = TONES[c.tone] || TONES.none;
        const on = weekOn(w.we);
        return (
            <React.Fragment key={w.we}>
                {gapN > 0 ? gap(`g${w.we}`, gapN) : null}
                <button
                    type="button"
                    className={`rw-cell${on ? ' on' : ''}`}
                    aria-pressed={on}
                    title={`${grid.weeks[w.we]?.title || `WE ${md(w.we)}`}\n\n${c.title}\nClick to pick the week, shift-click to take in the weeks between`}
                    style={{ width: COL_W * (w.b - w.a + 1) - 2, background: t.bg, color: t.fg }}
                    onClick={(e) => onWeek(w.we, e.shiftKey)}
                >
                    {lead ? <span style={{ fontWeight: 600, color: 'var(--foreground)' }}>{`WE ${md(w.we)}`}</span> : null}
                    {c.word ? <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{c.word}</span> : null}
                </button>
            </React.Fragment>
        );
    });

    const monthBlock = (blk, n) => (
        <div key={blk.key} style={{ marginTop: n ? 6 : 0 }}>
            <div style={{ display: 'flex', alignItems: 'flex-end', height: 18, borderBottom: '1px solid var(--border-strong)' }}>
                <span style={{ ...lbl, ...sticky, width: LABEL_W, flexShrink: 0, fontWeight: 600, color: 'var(--foreground)', lineHeight: '18px' }}>{blk.name}</span>
                {dayCells(blk, (x) => {
                    const m = moment(x.d, 'YYYY-MM-DD');
                    const text = `${m.format('dd').charAt(0)} ${m.date()}`;
                    const style = { color: m.day() === 0 ? 'var(--foreground)' : 'var(--muted-foreground)', fontWeight: x.d === today ? 600 : 400 };
                    if (!dayPick) return <span key={x.d} className="rw-day" title={m.format('ddd M/D/YY')} style={style}>{text}</span>;
                    const on = dayOn(x.d);
                    return (
                        <button
                            key={x.d}
                            type="button"
                            className={`rw-day${on ? ' on' : ''}`}
                            aria-pressed={on}
                            aria-label={m.format('ddd M/D/YY')}
                            title={`${m.format('ddd M/D/YY')} — click to pick the day, shift-click to take in the days between`}
                            style={style}
                            onClick={(e) => onDay(x.d, e.shiftKey)}
                        >
                            {text}
                        </button>
                    );
                })}
            </div>
            {['Day', 'Night'].filter((sh) => grid.sheets[sh]).map((sh) => (
                <Row key={sh} label={`${sh} ticket`} title={`The ${sh.toLowerCase()} crew's daily field ticket, by where its send stands`}>
                    {dayCells(blk, (x) => {
                        const c = grid.sheets[sh][x.d];
                        return c ? <Chip key={x.d} tone={c.tone} label={c.word} title={c.title} /> : gap(x.d, 1);
                    })}
                </Row>
            ))}
            <Row label="Week summary" title="The week summary the client signs — click a week to pick it">
                {weekRow(blk, summaryCell, true)}
            </Row>
            <Row label="Invoice" title="The labor and equipment invoices that bill the week — click a week to pick it">
                {weekRow(blk, invoiceCell, false)}
            </Row>
        </div>
    );

    return (
        <div>
            <style>{CSS}</style>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                <div role="group" aria-label="Weeks" style={{ flex: 1, minWidth: 0, overflowX: 'auto', paddingBottom: 4, scrollbarWidth: 'thin', scrollbarColor: 'var(--border) transparent' }}>
                    <div style={{ minWidth: LABEL_W + COL_W * slots }}>
                        {months.map(monthBlock)}
                    </div>
                </div>
                <FilterMenu
                    active={all || only || extraActive}
                    hint={dayPick ? 'Click days or weeks on the strip to pick them, shift-click to take in a run.' : 'Click weeks on the strip to pick them, shift-click to take in a run.'}
                >
                    <div className="rw-menu-h">Weeks</div>
                    <div style={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                        <button type="button" className={`rw-ghost${all ? ' on' : ''}`} aria-pressed={all} onClick={onAll}>All weeks</button>
                        <button type="button" className={`rw-ghost${only ? ' on' : ''}`} aria-pressed={only} title={only ? 'Show the earlier months again' : 'Hide every month before this one'} onClick={() => setOnlyTo(!only)}>This month</button>
                        <button type="button" className="rw-ghost" title="Show one more month back" onClick={() => { setOnlyTo(false); onMore(); }}>Earlier month</button>
                    </div>
                    {extra ? <><div className="rw-menu-sep" />{extra}</> : null}
                </FilterMenu>
            </div>
        </div>
    );
};

export default ReconWeekStrip;
